/* eslint-disable linebreak-style */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Modal, Label } from "semantic-ui-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ProviderProductDiscounts, { ProductDiscount, Product } from "../stores/ProviderProductDiscounts";
import PromiseButton from "../components/element/PromiseButton";

const Form = observer(({ productName, editMode, discountFormData, products, onInputChangeCallback }) => (
  <form>
    <div>
      <label htmlFor="product">Product</label>
      <br />
      {editMode ? (
        <strong>{productName}</strong>
      ) : (
        <select
          name="product"
          value={discountFormData.productId || ""}
          onChange={event => onInputChangeCallback("productId", parseInt(event.target.value, 10))}
        >
          <option value="">--choose a product--</option>
          {products.map(product => (
            <option key={product.productId} value={product.productId}>
              {product.name} - {product.creditCost} Credits
            </option>
          ))}
        </select>
      )}
    </div>
    <br />
    <div>
      <label htmlFor="creditCost">New Credit Cost</label>
      <br />
      <input
        type="text"
        name="creditCost"
        value={discountFormData.creditCost || ""}
        onChange={event => onInputChangeCallback("creditCost", event.target.value)}
      />
    </div>
    <br />
    <div>
      <label htmlFor="start">Start</label>
      <DatePicker
        selected={discountFormData.start ? discountFormData.start.toDate() : null}
        onChange={date => onInputChangeCallback("start", date)}
      />
    </div>
    <br />
    <div>
      <label htmlFor="stop">Stop</label>
      <DatePicker
        selected={discountFormData.stop ? discountFormData.stop.toDate() : null}
        onChange={date => onInputChangeCallback("stop", date)}
      />
      <a
        href="#"
        onClick={event => {
          event.preventDefault();
          onInputChangeCallback("stop", null);
        }}
      >
        clear
      </a>
    </div>
  </form>
));

Form.propTypes = {
  discountFormData: PropTypes.instanceOf(ProductDiscount),
  products: PropTypes.arrayOf(PropTypes.instanceOf(Product)),
  productName: PropTypes.string,
  editMode: PropTypes.bool,
};

const SingleTableColumn = ({ children }) => (
  <tr>
    <td colSpan="5">{children}</td>
  </tr>
);

SingleTableColumn.propTypes = {
  children: PropTypes.node.isRequired,
};

const LinkButton = observer(({ label, onClickCallback }) => (
  <a
    onClick={event => {
      event.preventDefault();
      onClickCallback();
    }}
    href="#"
  >
    {label}
  </a>
));

LinkButton.propTypes = {
  label: PropTypes.string.isRequired,
  onClickCallback: PropTypes.func.isRequired,
};

const TrDiscount = observer(({ discount, product, onDeleteCallback, onEditCallback }) => (
  <tr>
    <td>
      {product.name} ({product.creditCost})
    </td>
    <td>{discount.creditCost}</td>
    <td>{discount.start.format("MM/DD/YYYY")}</td>
    <td>{discount.stop ? discount.stop.format("MM/DD/YYYY") : ""}</td>
    <td>
      <LinkButton label="delete" onClickCallback={onDeleteCallback} /> &nbsp;
      <LinkButton label="edit" onClickCallback={onEditCallback} />
    </td>
  </tr>
));

@observer
class ProviderProductDiscountsWidget extends Component {
  constructor(props) {
    super(props);

    this.state = {
      editMode: false,
      productName: "",
    };
  }

  getContent() {
    const { store, providerId } = this.props;
    const { discounts, products, remainingProducts, loading } = store;

    if (loading) {
      return (
        <tbody>
          <SingleTableColumn>Loading...</SingleTableColumn>
        </tbody>
      );
    }

    const addButton = (
      <button
        type="button"
        className="button"
        onClick={() => {
          this.setState({ editMode: false, productName: "" });
          store.showModalForm();
        }}
      >
        Add
      </button>
    );

    if (discounts.length === 0) {
      return (
        <tbody>
          <SingleTableColumn>No discounts yet. Click Add button below.</SingleTableColumn>
          <SingleTableColumn>{addButton}</SingleTableColumn>
        </tbody>
      );
    }

    return (
      <tbody>
        {discounts.map(discount => (
          <TrDiscount
            key={discount.productDiscountId}
            discount={discount}
            product={products.find(product => product.productId === discount.productId)}
            onDeleteCallback={() => store.delete(providerId, discount)}
            onEditCallback={() => {
              this.setState({
                editMode: true,
                productName: products.find(product => product.productId === discount.productId).name,
              });
              store.showModalForm(discount);
            }}
          />
        ))}
        <SingleTableColumn>
          {remainingProducts.length === 0 ? <Label>No products left without a discount.</Label> : addButton}
        </SingleTableColumn>
      </tbody>
    );
  }

  render() {
    const { store, providerId } = this.props;
    const { editMode, productName } = this.state;
    const { modalFormOpen, remainingProducts, discountFormData } = store;

    return (
      <div>
        <Modal open={modalFormOpen} closeIcon="close" onClose={() => store.closeModalForm()} size="small">
          <Modal.Header>
            <strong>Product Discount</strong>
          </Modal.Header>
          <Modal.Content>
            <Form
              editMode={editMode}
              productName={productName}
              products={remainingProducts}
              discountFormData={discountFormData}
              onInputChangeCallback={(name, value) => store.onInputChange(name, value)}
            />
          </Modal.Content>
          <Modal.Actions>
            <a
              href="#"
              onClick={event => {
                event.preventDefault();
                store.closeModalForm();
              }}
            >
              cancel
            </a>
            <PromiseButton className="ui button black" onClick={() => store.save(providerId)}>
              Save
            </PromiseButton>
          </Modal.Actions>
        </Modal>
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>New Credit Cost</th>
              <th>Start</th>
              <th>End</th>
              <th />
            </tr>
          </thead>
          {this.getContent()}
        </table>
      </div>
    );
  }
}

ProviderProductDiscountsWidget.propTypes = {
  store: PropTypes.instanceOf(ProviderProductDiscounts),
  providerId: PropTypes.number.isRequired,
};

export default ProviderProductDiscountsWidget;
